import './App.css'
import React, { useEffect } from 'react'
import {
  Routes,
  Route,
  BrowserRouter as Router,
  useLocation,
  useNavigate,
} from 'react-router-dom'
import Login from './auth/Login'
import { AuthProvider, useAuth } from './context/AuthContext'
import { setAuthRedirect } from './api/axios-instance'
import { initializeMocks } from './mocks/mockControl'
import Navigation from './components/NavigationBar'
import ProtectedRoute from './components/misc/ProtectedRoute'
import Logout from './auth/Logout'
import ModelListPage from './components/models/model_list_page/ModelList.page'
import Profile from './components/Profile.page'
import ProjectList from './components/projects/project_list_page/ProjectList.page'
import ProjectPage from './components/projects/project_page/Project.page'
import ClientListPage from './components/clients/client_list_page/ClientList.page'

import NewModelPage from './components/models/new_model_page/NewModel.page'
import DatabookPage from './components/datasets/databook_page/Databook.page'
import ManageFAQPage from './components/learning/manage_faq_page/ManageFAQ.page'
import ManageLearning from './components/learning/manage_learning_page/ManageLearning.page'
import FAQPage from './components/learning/faq_page/FAQ.page'
import ManageIntroLearningPage from './components/learning/manage_intro_learning_page/ManageIntroLearning.page'
import IntroLearningPage from './components/learning/intro_learning_page/IntroLearning.page'
import ManageAcademyPage from './components/learning/manage_academy_page/ManageAcademy.page'
import AcademyPage from './components/learning/academy_page/Academy.page'
import BuildModelPage from './components/models/build_model_page/BuildModel.page'
import BuildScenarioPage from './components/scenario/build_scenario_page/BuildScenario.page'
import DashboardPage from './components/Dashboard.page'
import {
  DocumentScreenBehaviour,
  DocumentBelowNavbarBehaviour,
  DocumentMainBehaviour,
} from './styles/Global.styles'
import ConfigProjectSolutionPage from './components/projects/config_project_solution_page/ConfigProjectSolution.page'

import theme from './styles/theme'
import { ThemeProvider } from '@emotion/react'
import AcceptInvitation from './components/misc/AcceptInvitation'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ProjectProvider } from './context/ProjectContext'
import { ClientProvider } from './context/ClientContext'
import { ClientProjectProvider } from './context/ClientProjectContext'
import { ScrollProvider, useScroll } from './context/ScrollContext'
import ClientPage from './components/clients/client_page/Client.page'
import Footer from './components/Footer'
import RunModelHistoryPage from './components/models/model_history_page/ModelHistory.page'
import ImpactReportPage from './components/reports/impact_report_page/ImpactReport.page'
import OptimizeReportPage from './components/reports/optimize_report_page/OptimizeReport.page'
import PromptsPage from './components/reports/comp_reusable/prompts_page/Prompts.page'
import { GlobalOverlayProvider } from './context/GlobalOverlayContext'
import GlobalOverlay from './components/misc/GlobalOverlay'
import ReportListPage from './components/reports/model_report_list_page/ReportList.page'
import ScenarioReportPage from './components/reports/scenario_report_page/ScenarioReport.page'
import ScenarioReportListPage from './components/reports/scenario_report_list_page/ScenarioReportList.page'

// Move QueryClient creation into useMemo to keep it stable across renders
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
      gcTime: 30 * 60 * 1000, // Keep unused data in cache for 30 minutes (renamed from cacheTime in v5)
    },
  },
})

function App(): React.JSX.Element {
  return (
    <ThemeProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ClientProvider>
            <ClientProjectProvider>
              <ProjectProvider>
                <ScrollProvider>
                  <GlobalOverlayProvider>
                    <AppContent />
                  </GlobalOverlayProvider>
                </ScrollProvider>
              </ProjectProvider>
            </ClientProjectProvider>
          </ClientProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  )
}

function Telemetry(): React.JSX.Element | null {
  const location = useLocation()
  useEffect(() => {
    import('./lib/telemetry')
      .then((m) => m.markRouteView(location.pathname))
      .catch(() => {})
  }, [location.pathname])
  return null
}

function AuthRedirectBinder(): null {
  const navigate = useNavigate()

  useEffect(() => {
    setAuthRedirect((path: string) => {
      navigate(path, { replace: true })
    })
    return () => setAuthRedirect(null)
  }, [navigate])

  return null
}

function AppContent(): React.JSX.Element {
  const { isAuthenticated } = useAuth()
  const { setMainContainer } = useScroll()

  useEffect(() => {
    if (isAuthenticated) {
      // Fetch user info or trigger any state updates needed after login
    }
  }, [isAuthenticated])

  // Initialize mocks if conditions are met (only runs when explicitly enabled)
  useEffect(() => {
    initializeMocks().catch(console.error)
  }, [])

  const isInviteRoute = window.location.pathname.startsWith('/invite/')

  return (
    <Router>
      <Telemetry />
      <AuthRedirectBinder />
      <div css={DocumentScreenBehaviour}>
        {!isInviteRoute && <Navigation />}

        <div
          css={DocumentBelowNavbarBehaviour}
          ref={setMainContainer} // Add container reference here
        >
          <div css={DocumentMainBehaviour}>
            <Routes>
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route path="/invite/:token" element={<AcceptInvitation />} />
            <Route
              path="/models/new"
              element={
                <ProtectedRoute>
                  <NewModelPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/models/build"
              element={
                <ProtectedRoute>
                  <BuildModelPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/scenarios/build"
              element={
                <ProtectedRoute>
                  <BuildScenarioPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/scenarios/report"
              element={
                <ProtectedRoute>
                  <ScenarioReportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/models"
              element={
                <ProtectedRoute>
                  <ModelListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/models/history"
              element={
                <ProtectedRoute>
                  <RunModelHistoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/insights/saved_reports"
              element={
                <ProtectedRoute>
                  <ReportListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/impact/reports/:reportId"
              element={
                <ProtectedRoute>
                  <ImpactReportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/impact/report"
              element={
                <ProtectedRoute>
                  <ImpactReportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/optimize/reports/:reportId"
              element={
                <ProtectedRoute>
                  <OptimizeReportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/optimize/report"
              element={
                <ProtectedRoute>
                  <OptimizeReportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/insights/saved_scenarios"
              element={
                <ProtectedRoute>
                  <ScenarioReportListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/scenarios/:reportId"
              element={
                <ProtectedRoute>
                  <ScenarioReportPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/datasets"
              element={
                <ProtectedRoute>
                  <DatabookPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects"
              element={
                <ProtectedRoute roles={['admin']}>
                  <ProjectList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:project"
              element={
                <ProtectedRoute roles={['admin']}>
                  <ProjectPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:project/solutions/:solution"
              element={
                <ProtectedRoute roles={['admin']}>
                  <ConfigProjectSolutionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients"
              element={
                <ProtectedRoute roles={['clientManager']}>
                  <ClientListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients/:client"
              element={
                <ProtectedRoute roles={['clientManager']}>
                  <ClientPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/learning"
              element={
                <ProtectedRoute roles={['admin']}>
                  <ManageLearning />
                </ProtectedRoute>
              }
            />
            <Route
              path="/learning/faq"
              element={
                <ProtectedRoute roles={['admin']}>
                  <ManageFAQPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/faq"
              element={
                <ProtectedRoute>
                  <FAQPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/learning/intro"
              element={
                <ProtectedRoute roles={['admin']}>
                  <ManageIntroLearningPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/intro"
              element={
                <ProtectedRoute>
                  <IntroLearningPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/learning/academy"
              element={
                <ProtectedRoute roles={['admin']}>
                  <ManageAcademyPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/academy"
              element={
                <ProtectedRoute>
                  <AcademyPage />
                </ProtectedRoute>
              }
            />
            <Route path="/login" element={<Login />} />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route path="/logout" element={<Logout />} />
            <Route
              path="/prompts"
              element={
                <ProtectedRoute roles={['admin']}>
                  <PromptsPage />
                </ProtectedRoute>
              }
            />
          </Routes>
          </div>
          <Footer />
        </div>
        <GlobalOverlay />
      </div>
    </Router>
  )
}

export default App
